import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Grid, IconButton, InputAdornment, InputBaseComponentProps, TextField } from '@material-ui/core';
import ReactInputMask from 'react-input-mask';
import { Control, useController } from 'react-hook-form';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { ProtectedField } from './ProtectedField';
import { DocumentNode } from 'graphql';
import { taxIdRegexValidation } from '@fluentsms/utils';
import ConfirmationDialog from '../ConfirmationDialog';
import { useTranslation } from '@fluentsms/i18n';
import { AutocompleteInput } from '../AutocompleteInput';
import { TaxIdMasks } from '../utils/TaxIdMasks';
import { TaxId, TaxIdType } from '@fluentsms/types-fluent';

type GetFieldProps = {
  (
    /** Name prop to apply to the Textfield
     * can be shallow string or complex object using lodash's get syntax
     * example:
     *
     * `'firstName'`
     *
     * `'phoneNumbers[0].extension'`
     **/
    name: string,
  ): {
    /**
     * name prop
     */
    name: string;
    /** error prop **/
    error: boolean;
    /** helperText prop **/
    helperText: string;
  };
};

type TaxIdTypes = Exclude<TaxIdType, 'RAC'>;

export interface TaxIdErrorState {
  hasError: boolean;
  message: string;
}

export interface TaxIdFieldValue {
  type: TaxIdTypes;
  taxId: string;
  taxIdMasked?: string;
}

export interface TaxIdFieldGroupV2Props {
  getFieldProps: GetFieldProps;
  taxIdTypeName?: string;
  taxIdNumberName: string;
  control?: Control<Record<string, any>>;
  defaultTaxIdType?: TaxIdTypes;
  hasTaxIdPermission?: boolean;
  getProtectedIdQuery?: DocumentNode;
  getProtectedIdVariables?: object;
  taxIdKey?: string;
  disabled?: boolean;
  isCompanyTab?: boolean;
}

const usePrevious = (value: string | any) => {
  const ref = useRef();
  useEffect(() => {
    if (value?.length) {
      ref.current = value;
    }
  });
  return ref.current;
};

export const TaxIdFieldGroupV2 = (props: TaxIdFieldGroupV2Props) => {
  const {
    getFieldProps,
    control,
    taxIdNumberName,
    taxIdTypeName = '',
    defaultTaxIdType = TaxId.EIN,
    hasTaxIdPermission = false,
    getProtectedIdQuery,
    getProtectedIdVariables,
    taxIdKey,
    disabled,
    isCompanyTab = false,
  } = props;
  const taxIdMasks = TaxIdMasks();

  const [hidden, setHidden] = useState<boolean>(true);
  const [isVisible, setVisible] = useState<boolean>(false);
  const [showProtectedField, setShowProtectedField] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [error, setError] = useState<TaxIdErrorState>({ hasError: false, message: '' });
  const [showEyeBall, setShowEyeBall] = useState(true);

  const { t } = useTranslation('orders');
  const { t: commonTranslation } = useTranslation('common');
  const textInputRef = useRef<HTMLInputElement>(null);

  const maskChar = 'X';

  const {
    field: { ref: taxIdTypeRef, ...taxIdTypeProps },
  } = useController({ name: taxIdTypeName, control, defaultValue: '' });
  const {
    field: { ref: taxIdNumberRef, ...taxIdNumberProps },
  } = useController({ name: taxIdNumberName, control, defaultValue: '' });

  const calculateMask = (value: string): string => {
    const length = value.length;
    const substringStart = length - (length > 4 ? 4 : length);
    return `${maskChar.repeat(length > 4 ? length - 4 : 0)}${value?.substring(substringStart)}`;
  };

  const [fieldValue, setFieldValue] = useState<TaxIdFieldValue>({
    type: defaultTaxIdType,
    taxId: '',
    taxIdMasked: '',
  });

  const taxIdValue = hidden ? fieldValue.taxIdMasked || taxIdNumberProps.value : fieldValue.taxId;
  const fallbackValue = usePrevious(
    !taxIdValue?.includes('X') || taxIdValue === fieldValue.taxId ? taxIdValue : taxIdValue,
  );

  const handleChange = (data: Partial<TaxIdFieldValue>) => {
    const newState = { ...fieldValue, ...data };
    newState.taxIdMasked = calculateMask(newState.taxId);
    setFieldValue(newState);

    let errorState = { hasError: false, message: '' };

    if (!data?.taxId?.replace(/[-_]/g, '').match(taxIdRegexValidation) && data.taxId) {
      errorState = { hasError: true, message: `${commonTranslation('taxIdFieldGroup.taxIdErrorMsg9Digits')}` };
      setIsEditing(true);
    }

    setError(errorState);

    if (!newState.taxId?.includes(maskChar) && !newState.taxId?.includes('_') && !errorState.hasError) {
      if (data.type) return taxIdTypeProps.onChange(data.type);
      taxIdNumberProps.onChange(newState.taxId);
      taxIdTypeProps.onChange(newState.type);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target?.value;

    if (value?.includes(maskChar)) {
      event.preventDefault();
      setShowConfirmation(true);
    } else {
      if (hidden) {
        handleChange({ taxId: value.substring(value.length - 1) });
        setHidden(false);
      }
      handleChange({ taxId: value.replace(/[-_]/g, '') });
    }
  };

  const handleBlur = () => {
    setHidden(true);
    setVisible(false);
    setIsEditing(false);
    setShowProtectedField(false);
  };

  const eyeballHandler = () => {
    if (!isVisible) {
      setShowProtectedField(true);
      setVisible(true);
      setHidden(false);
    } else {
      handleBlur();
    }
  };

  const IdFieldProps = {
    ...getFieldProps(taxIdNumberName),
    onBlur: taxIdNumberProps.onBlur,
    'data-testid': 'taxId-number',
  };

  const TypeFieldProps = {
    'data-testid': 'taxId-type',
  };

  useEffect(() => {
    if (!taxIdNumberProps.value?.includes(maskChar) && isCompanyTab) {
      setShowEyeBall(false);
    } else {
      setShowEyeBall(true);
    }
  }, [isCompanyTab, taxIdNumberProps.value]);

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={5}>
          <AutocompleteInput
            disabled={disabled}
            value={taxIdTypeProps.value || fieldValue.type}
            clearOnEscape={true}
            options={Object.keys(taxIdMasks)}
            onChange={(_event, value) => handleChange({ type: value as TaxIdTypes })}
            renderInput={(param) => (
              <TextField {...TypeFieldProps} label={commonTranslation('taxIdFieldGroup.taxIdTypeLabel')} {...param} />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={7}>
          {showProtectedField ? (
            <ProtectedField
              value={taxIdValue}
              fallbackValue={fallbackValue}
              taxIdKey={taxIdKey}
              isEditing={isEditing}
              variables={getProtectedIdVariables}
              query={getProtectedIdQuery}
              onBlur={handleBlur}
              onChange={onChange}
              isVisible={isVisible}
              hasTaxIdPermission={hasTaxIdPermission}
              error={error}
              label={taxIdMasks[fieldValue.type]?.label}
              mask={taxIdMasks[fieldValue.type]?.mask}
            />
          ) : (
            <TextField
              fullWidth
              disabled={disabled}
              {...IdFieldProps}
              InputLabelProps={{
                shrink: !isVisible,
              }}
              error={error.hasError}
              helperText={error.message}
              value={taxIdValue}
              onBlur={handleBlur}
              label={taxIdMasks[fieldValue.type]?.label}
              onChange={onChange}
              InputProps={{
                inputComponent: (ReactInputMask as unknown) as FunctionComponent<InputBaseComponentProps>,
                ref: textInputRef,
                inputProps: {
                  inputcomponent: ReactInputMask,
                  mask: taxIdMasks[fieldValue.type]?.mask,
                },
                endAdornment:
                  hasTaxIdPermission && showEyeBall ? (
                    <InputAdornment position="end" tabIndex={-1}>
                      <IconButton
                        size="small"
                        tabIndex={-1}
                        disabled={taxIdValue === '' || error.hasError || disabled}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          eyeballHandler();
                        }}
                      >
                        {!isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
              }}
            />
          )}
        </Grid>
      </Grid>
      <ConfirmationDialog
        open={showConfirmation}
        autoFocus={false}
        dialogTitle={t('buyerSellerConfirmationText.editMaskedField')}
        dialogText={t('buyerSellerConfirmationText.editMaskedFieldDialogText')}
        primaryActionLabel={t('buyerSellerConfirmationText.continue')}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => {
          setFieldValue({ taxId: '', taxIdMasked: '', type: fieldValue.type });
          setShowConfirmation(false);
          setHidden(false);
        }}
      />
    </>
  );
};
